import  { useRef, useState } from 'react';
import { TextField, Button, Typography, IconButton, Paper, Grid, Card, CardContent, Box, Chip, alpha, FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import Header from './Header';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
interface Release {
  name: string;
  repo_link: string;
  tag: string;
  release_document: string;
  note: string;
  description: string;
  deployment_version_url: string;
}

const ReleasePlannerForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    version: '',
    description: '',
    milestone_name: '',
    release_date: '',
    priority: '',
    releases: [] as Release[],
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [editingRelease, setEditingRelease] = useState<number | null>(null);
  const [releaseForm, setReleaseForm] = useState<Release>({
    name: '',
    repo_link: '',
    tag: '',
    release_document: '',
    note: '',
    description: '',
    deployment_version_url: ''
  });
  const [isDragOver, setIsDragOver] = useState(false);

  const handleChange = (e: any) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleReleaseFormChange = (e: any) => {
    setReleaseForm({ ...releaseForm, [e.target.name]: e.target.value });
  };

  const openAddDialog = () => {
    setEditingRelease(null);
    setReleaseForm({
      name: '',
      repo_link: '',
      tag: '',
      release_document: '',
      note: '',
      description: '',
      deployment_version_url: ''
    });
    setOpenDialog(true);
  };

  const openEditDialog = (release: Release, index: number) => {
    setEditingRelease(index);
    setReleaseForm(release);
    setOpenDialog(true);
  };

  const handleSaveRelease = () => {
    if (editingRelease !== null) {
      // Edit existing release
      const updatedReleases = [...formData.releases];
      updatedReleases[editingRelease] = releaseForm;
      setFormData({ ...formData, releases: updatedReleases });
    } else {
      // Add new release
    setFormData({
      ...formData,
        releases: [...formData.releases, releaseForm],
    });
    }
    setOpenDialog(false);
  };

  const removeRelease = (index: number) => {
    const updatedReleases = [...formData.releases];
    updatedReleases.splice(index, 1);
    setFormData({ ...formData, releases: updatedReleases });
  };

  // Removed unused handleSubmit function

  // Build download JSON using user's schema
  const downloadSchemaData = {
    name: formData.name || 'Release Planner',
    version: formData.version,
    description: formData.description,
    milestone_name: formData.milestone_name,
    release_date: formData.release_date,
    priority: formData.priority,
    releases: formData.releases.map((release) => ({
      name: release.name,
      repo_link: release.repo_link,
      tag: release.tag,
      release_document: release.release_document,
      note: release.note,
      description: release.description,
      deployment_version_url: release.deployment_version_url
    }))
  };

  const handleDownload = () => {
    try {
      const jsonString = JSON.stringify(downloadSchemaData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeName = formData.name?.trim()?.replace(/\s+/g, '-') || 'release-plan';
      link.href = url;
      link.download = `${safeName}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download JSON:', error);
    }
  };

  // CSV Export Function
  const handleCSVExport = () => {
    try {
      const safeName = formData.name?.trim()?.replace(/\s+/g, '-') || 'release-plan';
      
      // Create CSV headers
      const headers = [
        'Service Name',
        'Repository Link', 
        'Tag',
        'Release Document',
        'Note',
        'Description',
        'Deployment Version URL'
      ];
      
      // Create CSV rows from releases data
      const csvRows = formData.releases.map(release => [
        release.name || '',
        release.repo_link || '',
        release.tag || '',
        release.release_document || '',
        release.note || '',
        release.description || '',
        release.deployment_version_url || ''
      ]);
      
      // Combine headers and data
      const csvContent = [headers, ...csvRows]
        .map(row => row.map(field => `"${field.replace(/"/g, '""')}"`).join(','))
        .join('\n');
      
      // Create and download CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeName}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export CSV:', error);
    }
  };

  // PDF Export Function
  const handlePDFExport = () => {
    try {
      console.log('Starting PDF export...');
      const safeName = formData.name?.trim()?.replace(/\s+/g, '-') || 'release-plan';
      
      // Create new PDF document
      const doc = new jsPDF();
      console.log('PDF document created');
      
      // Add title
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Release Plan Report', 20, 30);
      
      // Add project information
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Project: ${formData.name || 'N/A'}`, 20, 50);
      doc.text(`Version: ${formData.version || 'N/A'}`, 20, 60);
      doc.text(`Milestone: ${formData.milestone_name || 'N/A'}`, 20, 70);
      doc.text(`Release Date: ${formData.release_date || 'N/A'}`, 20, 80);
      doc.text(`Priority: ${formData.priority || 'N/A'}`, 20, 90);
      
      if (formData.description) {
        doc.text(`Description: ${formData.description}`, 20, 100);
      }
      
      // Add releases information
      let yPosition = 120;
      if (formData.releases.length > 0) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Release Components:', 20, yPosition);
        yPosition += 10;
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        
        formData.releases.forEach((release, index) => {
          if (yPosition > 280) { // Start new page if needed
            doc.addPage();
            yPosition = 20;
          }
          
          doc.setFont('helvetica', 'bold');
          doc.text(`${index + 1}. ${release.name || 'Unnamed Component'}`, 20, yPosition);
          yPosition += 8;
          
          doc.setFont('helvetica', 'normal');
          if (release.repo_link) {
            doc.text(`Repository: ${release.repo_link}`, 25, yPosition);
            yPosition += 6;
          }
          if (release.tag) {
            doc.text(`Tag: ${release.tag}`, 25, yPosition);
            yPosition += 6;
          }
          if (release.deployment_version_url) {
            doc.text(`Deployment URL: ${release.deployment_version_url}`, 25, yPosition);
            yPosition += 6;
          }
          if (release.description) {
            doc.text(`Description: ${release.description}`, 25, yPosition);
            yPosition += 6;
          }
          if (release.note) {
            doc.text(`Note: ${release.note}`, 25, yPosition);
            yPosition += 6;
          }
          yPosition += 10; // Space between components
        });
      } else {
        doc.text('No release components configured', 20, yPosition);
      }
      
      // Save the PDF
      console.log('Saving PDF...');
      doc.save(`${safeName}.pdf`);
      console.log('PDF saved successfully');
    } catch (error) {
      console.error('Failed to export PDF:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Failed to export PDF: ${errorMessage}`);
    }
  };

  // Upload and populate from JSON
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const handleUploadClick = () => fileInputRef.current?.click();
  
  const processJSONFile = (file: File) => {
    if (file.type && file.type !== 'application/json' && !file.name.endsWith('.json')) {
      console.error('Please upload a valid .json file');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse((reader.result as string) || '{}');
        const releasesArray = Array.isArray(parsed.releases) ? parsed.releases : [];
        const mappedReleases = releasesArray.map((r: any) => ({
          name: r?.name || '',
          repo_link: r?.repo_link || '',
          tag: r?.tag || '',
          release_document: r?.release_document || '',
          note: r?.note || '',
          description: r?.description || '',
          deployment_version_url: r?.deployment_version_url || ''
        }));
        setFormData({
          name: parsed?.name || '',
          version: parsed?.version || '',
          description: parsed?.description || '',
          milestone_name: parsed?.milestone_name || '',
          release_date: parsed?.release_date || '',
          priority: parsed?.priority || '',
          releases: mappedReleases
        });
      } catch (err) {
        console.error('Invalid JSON file', err);
        alert('Invalid JSON file. Please check the file format and try again.');
      }
    };
    reader.readAsText(file);
  };

  const handleFileChange = (e: any) => {
    const file: File | undefined = e?.target?.files?.[0];
    if (!file) return;
    processJSONFile(file);
    // reset so same file can be uploaded again
    e.target.value = '';
  };

  // Drag and Drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      processJSONFile(file);
    }
  };

  const formComp = () => {
    return (
      <Box sx={{ 
        width: '70%', 
        maxWidth: '1200px', 
        mx: 'auto', 
        px: { xs: 2, sm: 3, md: 3 }
      }}>
        {/* Project Overview Card */}
        <Paper 
          elevation={6} 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
              zIndex: 1
            }
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box sx={{ 
                p: 2, 
                mr: 3, 
                borderRadius: 2, 
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography variant="h3">📋</Typography>
              </Box>
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
            Release Plan Editor
          </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  Create and manage your release milestones
                </Typography>
              </Box>
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12} lg={4}>
            <TextField
              fullWidth
              label="Application Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
                  variant="filled"
                  size="small"
                  InputLabelProps={{ shrink: true, sx: { color: 'text.primary', '&.Mui-focused': { color: 'primary.main' } } }}
                  sx={{ 
                    '& .MuiFilledInput-root': { 
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      borderRadius: 2,
                      '&:hover': { backgroundColor: 'white' },
                      '&.Mui-focused': { backgroundColor: 'white' }
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
            <TextField
              fullWidth
              label="Version"
              name="version"
              value={formData.version}
              onChange={handleChange}
                  required
                  variant="filled"
                  size="small"
                  InputLabelProps={{ shrink: true, sx: { color: 'text.primary', '&.Mui-focused': { color: 'primary.main' } } }}
                  sx={{ 
                    '& .MuiFilledInput-root': { 
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      borderRadius: 2,
                      '&:hover': { backgroundColor: 'white' },
                      '&.Mui-focused': { backgroundColor: 'white' }
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <TextField
                  fullWidth
                  label="Milestone Name"
                  name="milestone_name"
                  value={formData.milestone_name}
                  onChange={handleChange}
                  required
                  variant="filled"
                  size="small"
                  InputLabelProps={{ shrink: true, sx: { color: 'text.primary', '&.Mui-focused': { color: 'primary.main' } } }}
                  sx={{ 
                    '& .MuiFilledInput-root': { 
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      borderRadius: 2,
                      '&:hover': { backgroundColor: 'white' },
                      '&.Mui-focused': { backgroundColor: 'white' }
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <TextField
                  fullWidth
                  label="Release Date"
                  name="release_date"
                  type="date"
                  value={formData.release_date}
                  onChange={handleChange}
              required
                  variant="filled"
                  size="small"
                  InputLabelProps={{ shrink: true, sx: { color: 'text.primary', '&.Mui-focused': { color: 'primary.main' } } }}
                  sx={{ 
                    '& .MuiFilledInput-root': { 
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      borderRadius: 2,
                      '&:hover': { backgroundColor: 'white' },
                      '&.Mui-focused': { backgroundColor: 'white' }
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <FormControl fullWidth variant="filled" required size="small">
                  <InputLabel id="priority-label" sx={{ color: 'text.primary', '&.Mui-focused': { color: 'primary.main' } }}>Priority Level</InputLabel>
                  <Select
                    labelId="priority-label"
                    label="Priority Level"
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    sx={{ 
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      borderRadius: 2,
                      '&:hover': { backgroundColor: 'white' },
                      '&.Mui-focused': { backgroundColor: 'white' },
                      '& .MuiSelect-select': { 
                        '&:focus': { backgroundColor: 'transparent' }
                      }
                    }}
                  >
                    <MenuItem value="critical">🚨 Critical</MenuItem>
                    <MenuItem value="high">⚡ High</MenuItem>
                    <MenuItem value="low">📋 Low</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} lg={4}>
            <TextField
              fullWidth
                  label="Project Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
                  rows={2}
                  variant="filled"
                  size="small"
                  InputLabelProps={{ shrink: true, sx: { color: 'text.primary', '&.Mui-focused': { color: 'primary.main' } } }}
                  sx={{ 
                    '& .MuiFilledInput-root': { 
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      borderRadius: 2,
                      '&:hover': { backgroundColor: 'white' },
                      '&.Mui-focused': { backgroundColor: 'white' }
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Release Components Management */}
        <Paper 
          elevation={5} 
          sx={{ 
            borderRadius: 3, 
            overflow: 'hidden',
            border: '1px solid rgba(33, 150, 243, 0.1)'
          }}
        >
          <Box sx={{ 
            p: 3, 
            background: 'linear-gradient(135deg, #2196f3 0%, #21cbf3 100%)',
            color: 'white',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
              zIndex: 1
            }
          }}>
            <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ 
                  p: 2, 
                  mr: 3, 
                  borderRadius: 2, 
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Typography variant="h4">🚀</Typography>
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Release Components
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    {formData.releases.length} component{formData.releases.length !== 1 ? 's' : ''} configured
                  </Typography>
                </Box>
              </Box>
              <Button
                startIcon={<AddIcon />}
                onClick={openAddDialog}
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  px: 3,
                  py: 1.25,
                  borderRadius: '25px',
                  fontWeight: 'bold',
                  fontSize: '0.95rem',
                  '&:hover': { 
                    backgroundColor: 'rgba(255,255,255,0.3)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(255,255,255,0.3)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Add Component
              </Button>
            </Box>
          </Box>

          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ 
                  backgroundColor: 'rgba(33, 150, 243, 0.05)',
                  '& .MuiTableCell-head': {
                    fontWeight: 'bold',
                    fontSize: '0.95rem',
                    color: 'primary.main',
                    borderBottom: '2px solid rgba(33, 150, 243, 0.1)'
                  }
                }}>
                  <TableCell>Service Name</TableCell>
                  <TableCell>Repository</TableCell>
                  <TableCell>Tag</TableCell>
                  <TableCell>Deployment URL</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formData.releases.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ 
                      textAlign: 'center', 
                      py: 8, 
                      color: 'text.secondary',
                      fontSize: '1.1rem'
                    }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                          🎯 No release components added yet
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                          Click "Add Component" to start building your release plan
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  formData.releases.map((release, index) => (
                    <TableRow 
                      key={`release-${index}-${release.name || 'empty'}`} 
                      hover
                      sx={{ 
                        '&:hover': { 
                          backgroundColor: 'rgba(33, 150, 243, 0.02)' 
                        },
                        '& .MuiTableCell-root': {
                          borderBottom: '1px solid rgba(0,0,0,0.05)'
                        }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 'medium', fontSize: '0.95rem' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ 
                            p: 1, 
                            mr: 2, 
                            borderRadius: 1, 
                            background: 'rgba(33, 150, 243, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Typography>⚙️</Typography>
                          </Box>
                          {release.name}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ 
                          maxWidth: 250, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: 'primary.main',
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' }
                        }}>
                          {release.repo_link}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={release.tag} 
                          size="small" 
                          variant="outlined"
                          sx={{ 
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            fontWeight: 'medium'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ 
                          maxWidth: 250, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: 'success.main',
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' }
                        }}>
                          {release.deployment_version_url}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ 
                          maxWidth: 300, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: 'text.secondary'
                        }}>
                          {release.description}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <IconButton 
                            onClick={() => openEditDialog(release, index)}
                            color="primary"
                            size="small"
                            sx={{ 
                              backgroundColor: 'rgba(33, 150, 243, 0.1)',
                              '&:hover': { 
                                backgroundColor: 'rgba(33, 150, 243, 0.2)',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            ✏️
                          </IconButton>
                          <IconButton 
                            onClick={() => removeRelease(index)}
                            color="error"
                            size="small"
                            sx={{ 
                              backgroundColor: 'rgba(244, 67, 54, 0.1)',
                              '&:hover': { 
                                backgroundColor: 'rgba(244, 67, 54, 0.2)',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <RemoveIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Drag and Drop Upload Area */}
        <Box sx={{ mt: 5, mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          
          {/* Drag and Drop Zone */}
          <Paper
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={handleUploadClick}
            sx={{
              width: '100%',
              maxWidth: '600px',
              minHeight: '200px',
              border: isDragOver 
                ? '3px dashed #2196f3' 
                : '2px dashed #e0e0e0',
              borderRadius: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              backgroundColor: isDragOver 
                ? 'rgba(33, 150, 243, 0.05)' 
                : 'rgba(0, 0, 0, 0.02)',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: '#2196f3',
                backgroundColor: 'rgba(33, 150, 243, 0.05)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(33, 150, 243, 0.15)',
              }
            }}
          >
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Box sx={{ 
                fontSize: '3rem', 
                mb: 2,
                opacity: isDragOver ? 1 : 0.7,
                transition: 'opacity 0.3s ease'
              }}>
                {isDragOver ? '📁' : '📄'}
              </Box>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 'bold', 
                  mb: 1,
                  color: isDragOver ? 'primary.main' : 'text.primary',
                  transition: 'color 0.3s ease'
                }}
              >
                {isDragOver ? 'Drop your JSON file here!' : 'Upload JSON File'}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'text.secondary',
                  mb: 2
                }}
              >
                Drag and drop your JSON file here, or click to browse
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.disabled',
                  fontStyle: 'italic'
                }}
              >
                Supports .json files only
              </Typography>
            </Box>
          </Paper>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2.5, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            onClick={handleDownload}
            variant="contained"
            color="primary"
            size="medium"
            sx={{ 
              px: 4,
              py: 2,
              borderRadius: '25px',
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: '1rem',
              minHeight: '48px',
              background: 'linear-gradient(45deg, #4caf50, #8bc34a)',
              '&:hover': {
                background: 'linear-gradient(45deg, #45a049, #7cb342)',
                transform: 'translateY(-3px)',
                boxShadow: '0 12px 35px rgba(76, 175, 80, 0.4)',
              },
              transition: 'all 0.3s ease'
            }}
          >
            📥 Download JSON
          </Button>
          
          <Button
            onClick={handleCSVExport}
            variant="contained"
            size="medium"
            startIcon={<TableChartIcon />}
            sx={{ 
              px: 4,
              py: 2,
              borderRadius: '25px',
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: '1rem',
              minHeight: '48px',
              background: 'linear-gradient(45deg, #ff9800, #ff5722)',
              '&:hover': {
                background: 'linear-gradient(45deg, #f57c00, #e64a19)',
                transform: 'translateY(-3px)',
                boxShadow: '0 12px 35px rgba(255, 152, 0, 0.4)',
              },
              transition: 'all 0.3s ease'
            }}
          >
            📊 Export CSV
          </Button>
          
          <Button
            onClick={handlePDFExport}
            variant="contained"
            size="medium"
            startIcon={<PictureAsPdfIcon />}
            sx={{ 
              px: 4,
              py: 2,
              borderRadius: '25px',
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: '1rem',
              minHeight: '48px',
              background: 'linear-gradient(45deg, #f44336, #d32f2f)',
              '&:hover': {
                background: 'linear-gradient(45deg, #e53935, #c62828)',
                transform: 'translateY(-3px)',
                boxShadow: '0 12px 35px rgba(244, 67, 54, 0.4)',
              },
              transition: 'all 0.3s ease'
            }}
          >
            📄 Export PDF
          </Button>
          
          <Button
            onClick={() => setFormData({
              name: '',
              version: '',
              description: '',
              milestone_name: '',
              release_date: '',
              priority: '',
              releases: []
            })}
            variant="outlined"
            size="medium"
            sx={{ 
              px: 4,
              py: 2,
              borderRadius: '25px',
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: '1rem',
              minHeight: '48px',
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                backgroundColor: 'rgba(33, 150, 243, 0.04)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(33, 150, 243, 0.3)',
              },
              transition: 'all 0.3s ease'
            }}
          >
            🔄 Clear All
          </Button>
          </Box>
        </Box>

        {/* Add/Edit Release Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={() => setOpenDialog(false)} 
          maxWidth="lg" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)'
            }
          }}
        >
          <DialogTitle sx={{ 
            p: 4, 
            background: 'linear-gradient(135deg, #2196f3 0%, #21cbf3 100%)',
            color: 'white',
            borderRadius: '16px 16px 0 0'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ 
                p: 1, 
                mr: 2, 
                borderRadius: 2, 
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography variant="h5">
                  {editingRelease !== null ? '✏️' : '➕'}
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {editingRelease !== null ? 'Edit Release Component' : 'Add New Release Component'}
            </Typography>
            </Box>
          </DialogTitle>
          
          <DialogContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Service Name"
                      name="name"
                  value={releaseForm.name}
                  onChange={handleReleaseFormChange}
                      required
                  variant="filled"
                  size="small"
                  sx={{ 
                    '& .MuiFilledInput-root': { 
                      backgroundColor: 'rgba(33, 150, 243, 0.05)',
                      borderRadius: 2,
                      '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.08)' },
                      '&.Mui-focused': { backgroundColor: 'rgba(33, 150, 243, 0.1)' }
                    }
                  }}
                    />
                  </Grid>
              <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Repository Link"
                      name="repo_link"
                  value={releaseForm.repo_link}
                  onChange={handleReleaseFormChange}
                      required
                  variant="filled"
                  size="small"
                  sx={{ 
                    '& .MuiFilledInput-root': { 
                      backgroundColor: 'rgba(33, 150, 243, 0.05)',
                      borderRadius: 2,
                      '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.08)' },
                      '&.Mui-focused': { backgroundColor: 'rgba(33, 150, 243, 0.1)' }
                    }
                  }}
                    />
                  </Grid>
              <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Tag"
                      name="tag"
                  value={releaseForm.tag}
                  onChange={handleReleaseFormChange}
                      required
                  variant="filled"
                  size="small"
                  sx={{ 
                    '& .MuiFilledInput-root': { 
                      backgroundColor: 'rgba(33, 150, 243, 0.05)',
                      borderRadius: 2,
                      '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.08)' },
                      '&.Mui-focused': { backgroundColor: 'rgba(33, 150, 243, 0.1)' }
                    }
                  }}
                    />
                  </Grid>
              <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Release Document Link"
                      name="release_document"
                  value={releaseForm.release_document}
                  onChange={handleReleaseFormChange}
                  variant="filled"
                  size="small"
                  sx={{ 
                    '& .MuiFilledInput-root': { 
                      backgroundColor: 'rgba(33, 150, 243, 0.05)',
                      borderRadius: 2,
                      '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.08)' },
                      '&.Mui-focused': { backgroundColor: 'rgba(33, 150, 243, 0.1)' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Deployment Version URL"
                  name="deployment_version_url"
                  value={releaseForm.deployment_version_url}
                  onChange={handleReleaseFormChange}
                  variant="filled"
                  size="small"
                  placeholder="Docker/Helm chart version URL"
                  sx={{ 
                    '& .MuiFilledInput-root': { 
                      backgroundColor: 'rgba(33, 150, 243, 0.05)',
                      borderRadius: 2,
                      '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.08)' },
                      '&.Mui-focused': { backgroundColor: 'rgba(33, 150, 243, 0.1)' }
                    }
                  }}
                    />
                  </Grid>
              <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Release Description"
                      name="description"
                  value={releaseForm.description}
                  onChange={handleReleaseFormChange}
                      multiline
                  rows={4}
                  variant="filled"
                  size="small"
                  sx={{ 
                    '& .MuiFilledInput-root': { 
                      backgroundColor: 'rgba(33, 150, 243, 0.05)',
                      borderRadius: 2,
                      '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.08)' },
                      '&.Mui-focused': { backgroundColor: 'rgba(33, 150, 243, 0.1)' }
                    }
                  }}
                    />
                  </Grid>
              <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Note"
                      name="note"
                  value={releaseForm.note}
                  onChange={handleReleaseFormChange}
                      multiline
                  rows={4}
                  variant="filled"
                  size="small"
                  sx={{ 
                    '& .MuiFilledInput-root': { 
                      backgroundColor: 'rgba(33, 150, 243, 0.05)',
                      borderRadius: 2,
                      '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.08)' },
                      '&.Mui-focused': { backgroundColor: 'rgba(33, 150, 243, 0.1)' }
                    }
                  }}
                    />
                  </Grid>
                  </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 3, gap: 1.5 }}>
            <Button
              onClick={() => setOpenDialog(false)}
              variant="outlined"
              size="medium"
              sx={{ 
                px: 3, 
                py: 1.25, 
                borderRadius: '20px',
                borderColor: 'grey.400',
                color: 'grey.600',
                '&:hover': {
                  borderColor: 'grey.600',
                  backgroundColor: 'rgba(0,0,0,0.04)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveRelease} 
              variant="contained"
              color="primary"
              size="medium"
              sx={{ 
                px: 4, 
                py: 1.25, 
                borderRadius: '20px',
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #2196f3, #21cbf3)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1976d2, #1cb5e0)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(33, 150, 243, 0.3)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {editingRelease !== null ? '✏️ Update' : '➕ Add'} Component
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };
  return (
    <Box sx={{ width: '100%', minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      <Header />

        {formComp()}

        {/* Give Back Section */}
      <Box sx={{ px: { xs: 2, sm: 4, md: 6, lg: 8 } }}>
        <Card
          sx={{
            mt: 4,
            mb: 2,
            background: `linear-gradient(135deg, ${alpha('#FFD700', 0.1)}, ${alpha('#2196f3', 0.1)})`,
            border: `1px solid ${alpha('#FFD700', 0.3)}`,
            borderRadius: '15px',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: `linear-gradient(90deg, #FFD700, #2196f3, #FFD700)`,
            },
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(45deg, #FFD700, #2196f3)`,
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                💛 Donate Back – Not Just Money
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary',
                  mb: 1,
                }}
              >
                If Release Pilot boosted your productivity by 0.1%—give back through <strong>impact, not money</strong> 🌟
              </Typography>
            </Box>

            <Box 
              sx={{ 
                display: 'flex', 
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 1,
                mb: 2,
              }}
            >
              {['🤝 Help others', '👥 Mentor fellow developer', '🚀 Share opportunities', '💡 Contribute skills', '❤️ Spread kindness'].map((item, index) => (
                <Chip
                  key={`help-chip-${item}`}
                  label={item}
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: alpha('#FFD700', 0.5),
                    color: 'text.primary',
                    '&:hover': {
                      backgroundColor: alpha('#FFD700', 0.1),
                      transform: 'scale(1.02)',
                    },
                  }}
                />
              ))}
            </Box>

            <Typography 
              variant="body2" 
              sx={{ 
                textAlign: 'center',
                color: 'text.secondary',
                fontStyle: 'italic',
              }}
            >
              Every small action matters. Let your gratitude become someone else's breakthrough. 🙌
            </Typography>
          </CardContent>
        </Card>

        {/* Crafted with Love */}
        <Typography 
          variant="body1" 
          sx={{ 
            textAlign: 'center',
            color: 'text.secondary',
            mt: 3,
            mb: 2,
            fontSize: '1.1rem',
          }}
        >
          Crafted with ❤️ in India
        </Typography>
      </Box>
    </Box>
  );
};

export default ReleasePlannerForm;
